import React from "react";

import { useFormikContext } from "formik";
import { GroupBase } from "react-select";

import { ImageEngineEnum } from "../../../../../enums/imageEngine";
import { Template } from "../../../../../models";
import { getImageEngineName, getImageEngineSVG } from "../../../../../utils";

import SelectV2, {
  OptionType,
} from "../../../../../components/form/CustomSelect";
import TemplateContainer from "../../common/TemplateContainer";

// Google deprecated gemini-2.5-flash-image in favor of the Nano Banana 2 family - checked 2026-07-27
// https://ai.google.dev/gemini-api/docs/image-generation
const DEPRECATED_IMAGE_ENGINES = new Set([
  ImageEngineEnum.GEMINI_2_5_FLASH_IMAGE,
]);

// what to use instead of each deprecated engine, shown in the info icon next to the "Deprecated" badge
const REPLACEMENT_IMAGE_ENGINE: Partial<
  Record<ImageEngineEnum, ImageEngineEnum>
> = {
  [ImageEngineEnum.GEMINI_2_5_FLASH_IMAGE]:
    ImageEngineEnum.GEMINI_3_1_FLASH_IMAGE,
};

// added 2026-07-27 - Nano Banana 2 Lite / Nano Banana 2 / Nano Banana Pro
const NEW_IMAGE_ENGINES = new Set([
  ImageEngineEnum.GEMINI_3_1_FLASH_LITE_IMAGE,
  ImageEngineEnum.GEMINI_3_1_FLASH_IMAGE,
  ImageEngineEnum.GEMINI_3_PRO_IMAGE,
]);

// must match primary_image_engine_id / secondary_image_engine_id in gen/gen_article.py
const PRIMARY_IMAGE_ENGINE = ImageEngineEnum.GEMINI_3_1_FLASH_IMAGE;
const SECONDARY_IMAGE_ENGINE = ImageEngineEnum.RECRAFT_V3;

// per-image cost (Standard tier, 1K resolution where tiered) - checked 2026-07-27
// https://ai.google.dev/gemini-api/docs/pricing
const IMAGE_ENGINE_INFO: Partial<
  Record<ImageEngineEnum, { costTooltip: string }>
> = {
  [ImageEngineEnum.GEMINI_2_5_FLASH_IMAGE]: { costTooltip: "$0.039 per image" },
  [ImageEngineEnum.GEMINI_3_1_FLASH_LITE_IMAGE]: {
    costTooltip: "$0.0336 per 1K image",
  },
  [ImageEngineEnum.GEMINI_3_1_FLASH_IMAGE]: {
    costTooltip: "$0.067 per 1K image",
  },
  [ImageEngineEnum.GEMINI_3_PRO_IMAGE]: {
    costTooltip: "$0.134 per 1K/2K image",
  },
};

const buildImageEngineOption = (engine: ImageEngineEnum) => {
  const isDeprecated = DEPRECATED_IMAGE_ENGINES.has(engine);
  const badge = isDeprecated
    ? ("deprecated" as const)
    : engine === PRIMARY_IMAGE_ENGINE
      ? ("primary" as const)
      : engine === SECONDARY_IMAGE_ENGINE
        ? ("secondary" as const)
        : NEW_IMAGE_ENGINES.has(engine)
          ? ("new" as const)
          : undefined;

  const replacement = REPLACEMENT_IMAGE_ENGINE[engine];
  const info = IMAGE_ENGINE_INFO[engine];

  return {
    value: engine,
    label: getImageEngineName(engine),
    icon: getImageEngineSVG(engine),
    description: "",
    isDisabled: isDeprecated,
    badge,
    badgeTooltip:
      isDeprecated && replacement
        ? `Use ${getImageEngineName(replacement)}`
        : undefined,
    costTooltip: info?.costTooltip,
  };
};

const OPENAI_IMAGE_ENGINES = [ImageEngineEnum.GPT_IMAGE_2];
const RECRAFT_IMAGE_ENGINES = [ImageEngineEnum.RECRAFT_V3];
const GEMINI_IMAGE_ENGINES = [
  ImageEngineEnum.GEMINI_3_1_FLASH_LITE_IMAGE,
  ImageEngineEnum.GEMINI_3_1_FLASH_IMAGE,
  ImageEngineEnum.GEMINI_3_PRO_IMAGE,
];
const DEPRECATED_IMAGE_ENGINES_ORDERED = [
  ImageEngineEnum.GEMINI_2_5_FLASH_IMAGE,
];

// flat list - used for looking up the currently selected engine's option
const imageEngineOptions = [
  ...OPENAI_IMAGE_ENGINES,
  ...RECRAFT_IMAGE_ENGINES,
  ...GEMINI_IMAGE_ENGINES,
  ...DEPRECATED_IMAGE_ENGINES_ORDERED,
].map(buildImageEngineOption);

// grouped by vendor (with deprecated grouped separately at the end) - used to render the dropdown
const imageEngineOptionGroups: GroupBase<OptionType>[] = [
  {
    label: "OpenAI",
    options: OPENAI_IMAGE_ENGINES.map(buildImageEngineOption),
  },
  {
    label: "Recraft",
    options: RECRAFT_IMAGE_ENGINES.map(buildImageEngineOption),
  },
  {
    label: "Gemini",
    options: GEMINI_IMAGE_ENGINES.map(buildImageEngineOption),
  },
  {
    label: "Deprecated",
    options: DEPRECATED_IMAGE_ENGINES_ORDERED.map(buildImageEngineOption),
  },
];

export default function ImageEngineSelector() {
  const { values, setFieldValue } = useFormikContext<Partial<Template>>();
  const selectedEngine = imageEngineOptions.find(
    (option) => option.value === values?.imageEngineId
  );

  return (
    <TemplateContainer
      isSuperUserFeature
      className="space-y-4"
    >
      <SelectV2
        label="Image Engine"
        placeholder="-"
        options={imageEngineOptionGroups}
        selected={selectedEngine}
        setSelected={(option) => {
          setFieldValue(
            "imageEngineId",
            (option?.value as ImageEngineEnum) || null
          );
          if (option?.value === ImageEngineEnum.RECRAFT_V3) {
            setFieldValue("excludeImageText", true);
            setFieldValue("excludeImageFaces", true);
          } else {
            setFieldValue("excludeImageText", null);
            setFieldValue("excludeImageFaces", null);
          }
        }}
      />
    </TemplateContainer>
  );
}
